import React, { useState, useEffect, useRef } from 'react'
import {
  analytics,
  PersonaliseEvent,
  useAnalyticsHandlers,
  useSendOnce,
} from '@moonpig/launchpad-analytics'
import { useSearchAnalytics } from '../../pages/SearchPage/analytics'
import {
  shoppingListImpressionEvent,
  shoppingListToggleEvent,
  shoppingListRemoveEvent,
} from '../../pages/SearchPage/events'
import {
  Box,
  Flex,
  PrimaryButton,
  Text,
  Image,
  Heading,
  IconButton,
  LoadingIndicator,
  SkeletonBox,
} from '@moonpig/launchpad-components'
import { breakpointUp, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { spacingPx, theme } from '@moonpig/launchpad-theme'
import { IconSystemBin, IconSystemChevronDown } from '@moonpig/launchpad-assets'
import {
  Status,
  useFetchMultiBuyList,
  useRemoveFromMultiBuyList,
  createPersonaliseGAEvent,
  PageTypeEnum,
} from '@moonpig/web-shared-products'
import { useFindLocaleText } from '../../text-localisation'
import { useStoreId } from '@moonpig/web-core-stores'
import {
  createGenericImpressionEvent,
  createGenericSelectContentEvent,
  trackGAEvent,
  useTrackGAEventOnce,
} from '@moonpig/web-core-analytics'
import { EmptyState } from './components/EmptyState'

const PANEL_MAX_WIDTH = '600px'
const PANEL_CORNER_RADIUS = '16px'
const ITEM_WIDTH = { xs: '56px', md: '70px' }
const ITEM_HEIGHT = { xs: '80px', md: '100px' }
const COLLAPSE_SCROLL_THRESHOLD = 40

const StyledCounter = styled.div`
  border-radius: 50%;
  ${s({
    bgcolor: 'colorBackgroundInformation',
    width: 24,
    height: 24,
    color: 'colorInteractionSelectedState',
    typography: 'typeBodyText',
    ml: 4,
  })}
`

const StyledFixedWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 999;
  pointer-events: none;
  padding-right: var(--removed-body-scroll-bar-size, 0px);
`

const StyledPanel = styled(Box)`
  pointer-events: auto;
  cursor: default;  
  ${s({
    bgcolor: 'colorBackground01',
    boxShadow: theme.shadows.boxShadow02,
    maxWidth: { md: PANEL_MAX_WIDTH },
    marginLeft: 'auto',
    px: 6,
  })}
  
  ${breakpointUp('md')} {
    margin-right: 24px;
  }

  border-top-left-radius: ${PANEL_CORNER_RADIUS};
  border-top-right-radius: ${PANEL_CORNER_RADIUS};
`

const StyledHeader = styled.button`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    ${s({ py: 5 })}
`

const StyledChevron = styled(IconSystemChevronDown)`
    flex-shrink: 0;
    transition: transform 200ms ease-in-out;
    ${s({
      color: 'colorInteractionIcon',
      width: 28,
      height: 28,
    })};

    &.collapsed {
        transform: rotate(180deg);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
`

const getExpandableHeight = (
  isExpanded: boolean,
  contentHeight: number | undefined,
) => {
  if (!isExpanded) return '0px'
  if (contentHeight === undefined) return 'auto'
  return `${contentHeight}px`
}

const StyledExpandable = styled.div<{
  $isExpanded: boolean
  $contentHeight: number | undefined
}>`
  height: ${({ $isExpanded, $contentHeight }) =>
    getExpandableHeight($isExpanded, $contentHeight)};
  overflow: hidden;
  transition: height 200ms ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const StyledItems = styled(Flex)<{ $showBorder: boolean }>`
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
  gap: ${spacingPx(5)};
  ${s({
    padding: 6,
    marginBottom: 6,
  })}
  ${({ $showBorder }) =>
    s({
      border: $showBorder ? '2px dashed' : 'none',
      borderColor: 'colorBorder03',
    })}  
`

const StyledItemImage = styled(Image)`
  ${s({
    boxShadow: theme.shadows.boxShadow01,
    width: '100%',
    height: '100%',
  })}
`

const StyledContentWidth = styled.div`
  pointer-events: none;
  ${({ theme: { siteWidth } }) => s({ maxWidth: siteWidth, mx: 'auto' })}
`

const StyledItemWrapper = styled(Box)`
  position: relative;
  flex: 0 0 auto;
`
const StyledDeleteButton = styled(IconButton)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
  z-index: 1;
  ${s({
    width: 28,
    height: 28,
    minHeight: 28,
    minWidth: 28,
    bottom: -6,
    bgcolor: 'colorBackground01',
    color: 'colorFeedbackError',
    boxShadow: theme.shadows.boxShadow01,
  })}

  svg {
    ${s({
      width: 16,
      height: 16,
    })}
  }
`

const handlePersonalise = (
  region: string,
  productKey: string,
  variantKey: string,
  productVariantSku: string,
  variant: string,
  listItemCount: number,
  isLaunchpadAnalytics: boolean,
) => {
  const event = createPersonaliseGAEvent({
    componentName: 'multibuy basket modal',
    value: listItemCount,
    productData: {
      id: productKey,
      variant,
      quantity: 1,
    },
  })
  // Launchpad event: apps/explore/find/src/pages/SearchPage/events.ts
  if (!isLaunchpadAnalytics) {
    trackGAEvent(event)
  }

  const url = `/${region}/customise/product/${productKey}/${variantKey}/?productVariantSku=${productVariantSku}`
  window.location.assign(url)
}

export const ShoppingList = ({
  context,
}: {
  context: PageTypeEnum.SEARCH | PageTypeEnum.GALLERY
}) => {
  const isLaunchpadAnalytics = useSearchAnalytics()

  const localiseText = useFindLocaleText()
  const { trackGAEventOnce } = useTrackGAEventOnce()
  const { sendOnce: sendAnalyticsOnce } = useSendOnce()
  const [isExpanded, setIsExpanded] = useState(true)
  const [contentHeight, setContentHeight] = useState<number>()
  const contentRef = useRef<HTMLDivElement>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isMissingFields, setIsMissingFields] = useState(false)
  const [itemsToDelete, setItemsToDelete] = useState(new Set<string>())
  const region = useStoreId()

  const { fetchMultiBuyListLoading, multiBuyListItems, multiBuyListItemCount } =
    useFetchMultiBuyList()

  const { removeFromMultiBuyList } = useRemoveFromMultiBuyList()

  const productIdForFirstItemInList = multiBuyListItems[0]?.variant.productId

  const variantKeyForFirstItemInList = multiBuyListItems[0]?.variant.key

  const productVariantSkuForFirstItemInList =
    multiBuyListItems[0]?.variant.sku ?? ''

  const productVariantTitleForFirstItemInList =
    multiBuyListItems[0]?.variant.title

  const personaliseData = productIdForFirstItemInList
    ? createPersonaliseGAEvent({
        componentName: 'multibuy basket modal',
        value: multiBuyListItemCount,
        productData: {
          id: productIdForFirstItemInList,
          variant: productVariantTitleForFirstItemInList,
          quantity: 1,
        },
      })
    : undefined

  useEffect(() => {
    const content = contentRef.current
    // Content is always mounted with the expandable panel; guard for type safety.
    /* istanbul ignore next */
    if (!content) return

    if (typeof ResizeObserver === 'undefined') return

    const updateHeight = () => setContentHeight(content.offsetHeight)
    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(content)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (isExpanded) {
      if (isLaunchpadAnalytics) {
        // Legacy impression: apps/explore/find/src/components/ShoppingList/ShoppingList.tsx
        sendAnalyticsOnce(
          shoppingListImpressionEvent().withEventData({
            category: context,
            label: 'multibuy basket modal | expanded',
            value: multiBuyListItemCount,
          }),
        )
      } else {
        trackGAEventOnce(
          createGenericImpressionEvent({
            category: context,
            action: 'view multibuy list',
            label: 'multibuy basket modal | expanded',
            value: multiBuyListItemCount,
          }),
        )
      }
    }
  }, [
    context,
    isExpanded,
    multiBuyListItemCount,
    isLaunchpadAnalytics,
    trackGAEventOnce,
    sendAnalyticsOnce,
  ])

  useEffect(() => {
    if (!isExpanded) return

    let scrollStart = Math.max(0, window.scrollY)
    const handleScroll = () => {
      const currentScroll = Math.max(0, window.scrollY)
      if (currentScroll < scrollStart) scrollStart = currentScroll
      if (currentScroll - scrollStart > COLLAPSE_SCROLL_THRESHOLD) {
        setIsExpanded(false)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isExpanded])

  const DeleteLoadingIcon = () => (
    <LoadingIndicator label={'Removing'} size={8} color="colorFeedbackError" />
  )

  const handleOnRemove = async (id: string) => {
    setErrorMessage(null)
    setItemsToDelete(prev => new Set(prev).add(id))

    const response = await removeFromMultiBuyList({ multiBuyListItemId: id })

    setItemsToDelete(prev => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })

    if (response.error?.errorMessage) {
      setErrorMessage(response.error.errorMessage)
    }

    if (!response.error) {
      if (isLaunchpadAnalytics) {
        // Legacy removal event: apps/explore/find/src/components/ShoppingList/ShoppingList.tsx
        analytics.send(
          shoppingListRemoveEvent()
            .withEventData({
              category: context,
              action: 'remove product from multibuy list',
              label: 'multibuy basket modal | delete',
              value:
                response.totalMultiBuyListItemCount === undefined
                  ? undefined
                  : response.totalMultiBuyListItemCount + 1,
            })
            .withContentData({
              content_type: `${context} | remove product from multibuy list | multibuy basket modal | delete`,
            }),
        )
      } else {
        // Launchpad event: apps/explore/find/src/pages/SearchPage/events.ts
        trackGAEvent(
          createGenericSelectContentEvent({
            eventCategory: context,
            eventAction: 'remove product from multibuy list',
            eventLabel: 'multibuy basket modal | delete',
            eventValue:
              response.totalMultiBuyListItemCount === undefined
                ? undefined
                : response.totalMultiBuyListItemCount + 1,
          }),
        )
      }
    }
  }

  const toggleExpanded = (source: 'chevron' | 'continue browsing') => {
    let eventAction = isExpanded
      ? 'collapse multibuy list'
      : 'expand multibuy list'

    if (source === 'continue browsing') {
      eventAction = 'continue'
    }

    setIsExpanded(prev => !prev)
    // Launchpad event: apps/explore/find/src/pages/SearchPage/events.ts
    if (!isLaunchpadAnalytics) {
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: context,
          eventAction,
          eventLabel: `multibuy basket modal | ${source}`,
          eventValue: source === 'chevron' ? multiBuyListItemCount : undefined,
        }),
      )
    }
  }

  const toggleAction = isExpanded
    ? 'collapse multibuy list'
    : 'expand multibuy list'
  // Legacy event: apps/explore/find/src/components/ShoppingList/ShoppingList.tsx
  const headerClickEvent = shoppingListToggleEvent()
    .withEventData({
      category: context,
      action: toggleAction,
      label: 'multibuy basket modal | chevron',
      value: multiBuyListItemCount,
    })
    .withContentData({
      content_type: `${context} | ${toggleAction} | multibuy basket modal | chevron`,
    })
  // Legacy event: apps/explore/find/src/components/ShoppingList/ShoppingList.tsx
  const continueClickEvent = shoppingListToggleEvent()
    .withEventData({
      category: context,
      action: 'continue',
      label: 'multibuy basket modal | continue browsing',
    })
    .withContentData({
      content_type: `${context} | continue | multibuy basket modal | continue browsing`,
    })

  const headerHandlers = useAnalyticsHandlers(
    isLaunchpadAnalytics ? { onClick: headerClickEvent } : {},
    { onClick: () => toggleExpanded('chevron') },
  )

  const reversedMultiBuyItems = [...multiBuyListItems].reverse()

  const hasListItems = multiBuyListItemCount > 0
  const isListEmpty = !fetchMultiBuyListLoading && !hasListItems

  return (
    <>
      <Status
        error={{
          status: !!errorMessage || isMissingFields,
          message:
            errorMessage ??
            (isMissingFields
              ? localiseText('find.something_went_wrong')
              : undefined),
          onDismiss: () => {
            setErrorMessage(null)
            setIsMissingFields(false)
          },
        }}
      />
      <StyledFixedWrapper>
        <StyledContentWidth>
          <StyledPanel
            role="region"
            aria-label={localiseText('find.shopping_list.title')}
            data-testid="shopping-list"
          >
            <StyledHeader
              type="button"
              data-testid="shopping-list-header"
              {...headerHandlers}
              aria-expanded={isExpanded}
              aria-label={
                isExpanded
                  ? localiseText('find.shopping_list_collapse')
                  : localiseText('find.shopping_list_expand')
              }
            >
              <Flex alignItems="center">
                <Heading
                  level="h3"
                  typography="typeDisplay05"
                  color="colorTextHeadline"
                  margin="0"
                >
                  {localiseText('find.shopping_list.title')}
                </Heading>
                {hasListItems && (
                  <StyledCounter data-testid="counter">
                    <Text fontWeight="bold">{multiBuyListItemCount}</Text>
                  </StyledCounter>
                )}
              </Flex>
              <StyledChevron
                className={isExpanded ? '' : 'collapsed'}
                aria-hidden
              />
            </StyledHeader>
            <StyledExpandable
              $isExpanded={isExpanded}
              $contentHeight={contentHeight}
              inert={!isExpanded}
              aria-hidden={!isExpanded}
            >
              <Box ref={contentRef}>
                <StyledItems $showBorder={isListEmpty}>
                  {fetchMultiBuyListLoading ? (
                    <SkeletonBox
                      width={ITEM_WIDTH}
                      minWidth={ITEM_WIDTH}
                      height={ITEM_HEIGHT}
                      data-testid="shopping-list-item-skeleton"
                    />
                  ) : (
                    reversedMultiBuyItems.map((item, index) => (
                      <StyledItemWrapper
                        width={ITEM_WIDTH}
                        minWidth={ITEM_WIDTH}
                        key={item.id}
                      >
                        <StyledItemImage
                          src={
                            item.variant.productImages.mainImage.small.jpgUrl
                          }
                          alt={item.variant.sku || ''}
                        />
                        <StyledDeleteButton
                          icon={
                            itemsToDelete.has(item.id)
                              ? DeleteLoadingIcon
                              : IconSystemBin
                          }
                          disabled={itemsToDelete.has(item.id)}
                          label={localiseText(
                            'find.shopping_list_remove_item',
                            {
                              cardPosition: index + 1,
                            },
                          )}
                          onClick={e => {
                            e.stopPropagation()
                            handleOnRemove(item.id)
                          }}
                        />
                      </StyledItemWrapper>
                    ))
                  )}
                  {isListEmpty && <EmptyState />}
                </StyledItems>
                {!isListEmpty && (
                  <PrimaryButton
                    width="100%"
                    loading={fetchMultiBuyListLoading}
                    events={
                      // Legacy event: packages/shared/products/src/analytics/createPersonaliseGAEvent.ts
                      isLaunchpadAnalytics &&
                      personaliseData &&
                      variantKeyForFirstItemInList
                        ? {
                            onClick: PersonaliseEvent()
                              .withEventData(personaliseData.event_data)
                              .withContentData(
                                personaliseData.content_data ?? {},
                              )
                              .withProductData(
                                personaliseData.product_data ?? {},
                              ),
                          }
                        : undefined
                    }
                    onClick={() => {
                      if (
                        !productIdForFirstItemInList ||
                        !variantKeyForFirstItemInList
                      ) {
                        setIsMissingFields(true)
                        return
                      }

                      handlePersonalise(
                        region,
                        productIdForFirstItemInList,
                        variantKeyForFirstItemInList,
                        productVariantSkuForFirstItemInList,
                        productVariantTitleForFirstItemInList,
                        multiBuyListItemCount,
                        isLaunchpadAnalytics,
                      )
                    }}
                  >
                    {!fetchMultiBuyListLoading &&
                      localiseText('find.shopping_list.cta.personalise')}
                  </PrimaryButton>
                )}
                {isListEmpty && (
                  <PrimaryButton
                    width="100%"
                    events={
                      isLaunchpadAnalytics
                        ? { onClick: continueClickEvent }
                        : undefined
                    }
                    onClick={() => toggleExpanded('continue browsing')}
                  >
                    {localiseText('find.shopping_list.cta.continue_browsing')}
                  </PrimaryButton>
                )}
                <Box pb={6} />
              </Box>
            </StyledExpandable>
          </StyledPanel>
        </StyledContentWidth>
      </StyledFixedWrapper>
    </>
  )
}
